import type React from "react"
import { useState, useRef } from "react"
import { Typography, IconButton, Paper } from "@mui/material"
import { CloudUpload, Delete, InsertDriveFile } from "@mui/icons-material"
import styles from "./FileUploader.module.css"

interface FileUploaderProps {
  name: string
  accept?: string
  maxSize?: number // in MB
  multiple?: boolean
  description?: string
  onFilesChange?: (files: File[]) => void
  disabled?: boolean
  required?: boolean
}

const FileUploader: React.FC<FileUploaderProps> = ({
  name,
  accept = "*",
  maxSize = 5,
  multiple = false,
  description,
  onFilesChange,
  disabled = false,
  required = false,
}) => {
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (selectedFiles: File[]) => {
    setError(null)

    // Check file size
    const oversizedFiles = selectedFiles.filter((file) => file.size > maxSize * 1024 * 1024)

    if (oversizedFiles.length > 0) {
      setError(`Some files exceed the maximum size of ${maxSize}MB`)
      return
    }

    let newFiles: File[]
    if (multiple) {
      newFiles = [...files, ...selectedFiles]
    } else {
      newFiles = selectedFiles
    }

    setFiles(newFiles)
    onFilesChange?.(newFiles)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    handleFileChange(selectedFiles)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)

    if (disabled) return

    const selectedFiles = Array.from(e.dataTransfer.files)
    handleFileChange(selectedFiles)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!disabled) {
      setDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
  }

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index)
    setFiles(newFiles)
    setError(null)
    onFilesChange?.(newFiles)

    // Reset the input value to allow re-uploading the same file
    if (inputRef.current) {
      inputRef.current.value = ""
    }
  }

  const handleButtonClick = () => {
    if (!disabled) {
      inputRef.current?.click()
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  return (
    <div className={styles.container}>
      <Paper
        className={`${styles.dropZone} ${dragOver ? styles.dragOver : ""} ${disabled ? styles.disabled : ""}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleButtonClick}
        elevation={dragOver ? 4 : 1}
      >
        <CloudUpload className={styles.uploadIcon} />
        <Typography variant="body1" className={styles.uploadText}>
          {dragOver ? "Drop files here" : "Click to upload or drag and drop"}
        </Typography>
        {description && (
          <Typography variant="body2" color="textSecondary" className={styles.description}>
            {description}
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary" className={styles.sizeLimit}>
          {multiple ? "Files" : "File"} up to {maxSize}MB • {accept === "*" ? "Any file type" : accept}
          {required && <span className={styles.required}> *</span>}
        </Typography>

        <input
          ref={inputRef}
          type="file"
          name={name}
          accept={accept}
          multiple={multiple}
          onChange={handleInputChange}
          className={styles.hiddenInput}
          disabled={disabled}
          required={required}
        />
      </Paper>

      {error && (
        <Typography variant="body2" color="error" className={styles.error}>
          {error}
        </Typography>
      )}

      {files.length > 0 && (
        <div className={styles.filesList}>
          <Typography variant="subtitle2" className={styles.filesTitle}>
            Selected Files ({files.length})
          </Typography>
          {files.map((file, index) => (
            <Paper key={index} className={styles.fileItem} elevation={1}>
              <div className={styles.fileInfo}>
                <InsertDriveFile className={styles.fileIcon} />
                <div className={styles.fileDetails}>
                  <Typography variant="body2" className={styles.fileName}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatFileSize(file.size)}
                  </Typography>
                </div>
              </div>
              {!disabled && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeFile(index)
                  }}
                  className={styles.removeButton}
                >
                  <Delete />
                </IconButton>
              )}
            </Paper>
          ))}
        </div>
      )}
    </div>
  )
}

export default FileUploader
